import { useCallback , useState, useMemo} from 'react';
import { Button } from "semantic-ui-react";
import Welcome from "../../UIelems/v2/Header/Welcome";
import ActivePrescriptionsDashboard from "../../../components/medications/ActivePrescriptionsDashboard";
import { LastOrder } from "../../UIelems/v2/LastOrder";
import AccountOnboarding from "../../../xAppLib/UIelems/AccountOnboarding";
import MFA from "../../../xAppLib/UIelems/MFA";
import { ActiveConsult } from "../../UIelems/v2/Header/ActiveConsult";
import NotificationBannersApp from "../../../notifications/view/app/NotificationBannersApp";
import CommonAlerts from "../../../xAppLib/UIelems/CommonAlerts";
import { useBreakpoint } from "@iscr/views/ordering/hooks/useBreakpoint";
import { useProfile, useUser } from "xAppLib/Hooks";
import { useOnePassDiscountStrings } from "xAppLib/Hooks/discounts/useOnePassDiscountStrings";
import onepass_model from "../../../models/onepass_model";
import { PUBLIC_DISCOUNT_KEYS } from "constants";
import { FooterDownload } from "../../UIelems/v2/footer/FooterDownload";
import { cls } from '../../NUI/utils';
import { TextImageBox } from "../../UIelems/v2/TextImageBox";
import {HeaderHome} from "../../UIelems/v2/Header/HeaderHome";
import { ARTICLES } from "./consts";
import ConsultWaitTimeIndicator from "@iscr/components/ConsultWaitTimeIndicator";

const LoggedInHomepage = ({exp}) => {
  const user = useUser();
  const [prof] = useProfile();
  const newUser = !prof?.hist.length;
  const showMFA = app.acl.is_usr;
  const isMobile = !useBreakpoint("lg");
  const showWaitTimeIndicator = exp?.waitTimeExp?.n === "show_wt" && !app.acl.is_work;
  const showFootnote = app.settings?.avg_wait_med;
  const hasBanners = app.site_status?.notifications?.enable_banners?.includes?.(
    app.runtime?.app
  );

  return (
    <div className={cls("flex flex-col lg:flex-row")}>
        {/* {!__IONIC__ && <Hero user_in={user_in && !publicView} about={about} />} */}
      <section>
          <HeaderHome user={user}/>
        </section>
    <div className="lg:p-4 flex justify-center gap-4 w-full">
      <div className="lg:mx-auto lg:py-12 flex flex-col gap-3 lg:px-0 md:max-w-[689px] xl:max-w-[737px]">
        <Welcome clsn="hidden lg:block" />
        {newUser && (
          <>
            <AccountOnboarding />
            {showMFA && <MFA />}
          </>
        )}

        <div className="hidden lg:block">
          <CommonAlerts />
        </div>

        {hasBanners && (
          <div className="hidden lg:block">
            <NotificationBannersApp />
          </div>
        )}

        {showWaitTimeIndicator && (
          <div className="hidden lg:block">
            <ConsultWaitTimeIndicator badge light color={user&&"lg:bg-is2-green"} />
            {showFootnote && <div className="text-sm mt-2 text-is2-gray-body ml-4">*Wait times may vary</div>}
          </div>
        )}

        <ActiveConsult clsn="block" skip={isMobile ? 1 : 0} />

        <LastOrder clsn="block lg:hidden bg-white px-6 py-9" />

        {!newUser && (
          <div className="h-fit block md:hidden">
            <BannersWrapper layout="side" />
          </div>
        )}

        <LastOrder clsn="lg:block hidden" />

        {!newUser && <ActivePrescriptionsDashboard cardQty={2} />}

        <BannersWrapper layout="main" title="What's new:" />

        <FooterDownload clsn="lg:hidden" />

        <Help />

      </div>

      {!newUser && (
        <div className="h-fit xl:max-w-[363px] hidden menu-xl:block">
          <BannersWrapper layout="side" user={user}/>
        </div>
      )}
    </div>
    </div>
  );
};

const useHydrateBannerData = ({user}) => {
  const onepassDiscountStrings = useOnePassDiscountStrings();
  const isOnePassMember = onepass_model.isValidOnepassMember();
  const isPulseRewardsMember = user?.prof?.pulsemembership?.member_number;

  return useCallback(
    (article) => {
      if (article.id === "onepass") {
        if (isOnePassMember) {
          return [];
        }
        if (onepassDiscountStrings) {
          const updatedDescription = `Get ${
            onepassDiscountStrings[PUBLIC_DISCOUNT_KEYS.ONE_PASS]
          } off eligible InstantScripts services and free standard delivery on prescription medication in Australia. T&Cs apply.`;
          return { ...article, desc: updatedDescription };
        }
      }
      if (article.id === "pulserewards" && isPulseRewardsMember) {
        return [];
      }
      return article;
    },
    [onepassDiscountStrings, isOnePassMember]
  );
};

const BannersWrapper = ({ layout, title, clsn, user }) => {
  const expiry = 1000 * 60 * 60 * 24;
  const [dismissed, setDismissed] = useState([]);
  const hydrateBannerData = useHydrateBannerData({user});
  const bannersList = useMemo(() => {
    const articlesToShow =
      (layout === "main" && ["consults", "certs", "delivery", "patho"]) ||
      (layout === "side" && ["refer", "pulserewards", "onepass"]);

    const isDismissed = (id) =>
      dismissed.includes(id) ||
      JSON.parse(sessionStorage.getItem(`${id}_home_dismissed`))?.expiry >
        new Date().getTime();

    return ARTICLES.filter(
      (article) =>
        articlesToShow.includes(article.id) && !isDismissed(article.id)
    ).flatMap((article) => hydrateBannerData(article));
  }, [dismissed, hydrateBannerData]);

  const hideBanners = (id) => {
    setDismissed([...dismissed, id]);
    sessionStorage.setItem(
      `${id}_home_dismissed`,
      JSON.stringify({ expiry: new Date().getTime() + expiry })
    );
  };

  return (
    <div
      className={cls(clsn, layout != "side" && "bg-white px-6 pt-9 rounded")}
    >
      {title && (
        <h2
          className={cls(
            layout === "main" ? "mb-9" : "mb-4",
            "hidden lg:block"
          )}
        >
          {title}
        </h2>
      )}
      <div className="flex flex-wrap gap-2 lg:gap-6">
        {bannersList.map((article, i) => {
          return (
            <TextImageBox
              key={i}
              {...article}
              col={layout === "side"}
              onDismiss={(id) => hideBanners(id)}
            />
          );
        })}
      </div>
    </div>
  );
};

const Help = () => {
  return (
    <div className="bg-is2-yellow-100 pt-14 pb-12 md:py-9 px-6 lg:rounded-[28px]">
      <h3 className="text-left lg:text-center mb-6">Need some help?</h3>
      <div className="flex flex-col md:flex-row gap-4 items-center md:justify-center">
        <Button
          basic
          className="!w-full"
          as={"a"}
          href="https://help.instantscripts.com.au/en/"
        >
          View our help articles
        </Button>
        <p className="mb-0 text-sm md:hidden">or</p>
        <Button
          basic
          className="!w-full"
          onClick={() => window.Intercom("show")}
        >
          Chat to us
        </Button>
      </div>
    </div>
  );
};
export default LoggedInHomepage;
